import { Injectable, Inject } from '@angular/core';
import { ENV_CONFIG } from 'src/app/core/injection.token';
import { ApiService } from 'src/app/core/services/api.service';
import { SpecialOfferEvents } from './events';
import { DEFAULT_PAGE_SIZE } from '../../constants/commonConstants';

type DynamicPaged<T> = app.models.DynamicResponse<app.models.PagedResult<T>>;

@Injectable()
export class SpecialOfferAPIService {
  public constructor(
    @Inject(ApiService) private readonly _apiService: ApiService,
    @Inject(ENV_CONFIG) private readonly _envConfig: env.EnvData
  ) {}

  private readonly controller = 'specialOffer';

  public saveSpecialOffer(requestBody: app.models.SaveSpecialOfferModel) {
    const request = 'saveSpecialOffer';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.postAsync<app.models.ServerResponseModel>(url, requestBody, [
      SpecialOfferEvents.SpecialOfferSaved,
    ]);
  }

  public getSpecialOffer(specialOfferId: string) {
    const request = 'getSpecialOffer';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      specialOfferId: specialOfferId,
    });
    return this._apiService.get<app.models.GetSpecialOfferModel>(url);
  }

  public getClientSpecialOffer(specialOfferId: string) {
    const request = 'getClientSpecialOffer';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      specialOfferId: specialOfferId,
    });
    return this._apiService.get<app.models.GetSpecialOfferClientModel>(url);
  }

  public deleteSpecialOffer(specialOfferId: string) {
    const request = 'deleteSpecialOffer';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const data = { specialOfferId: specialOfferId };
    return this._apiService.postAsync<app.models.ServerResponseModel>(url, data, [
      SpecialOfferEvents.SpecialOfferDelete,
    ]);
  }

  public getFilteredSpecialOffers(pageIndex: number, pageFilter: app.models.GetSpecialOffersFilterModel) {
    let data = {
      ...pageFilter,
      fromDate: pageFilter.fromDate ? pageFilter.fromDate.toISOString() : null,
      toDate: pageFilter.toDate ? pageFilter.toDate.toISOString() : null,
      page: pageIndex,
      size: DEFAULT_PAGE_SIZE,
    };
    const request = 'getFilteredSpecialOffers';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.postGet<DynamicPaged<app.models.GetFilteredSpecialOfferModel>>(url, data);
  }

  public getSpecialOfferInsights(specialOfferId: string) {
    const request = 'getSpecialOfferInsights';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      specialOfferId: specialOfferId,
    });
    return this._apiService.get<app.models.GetSpecialOfferInsightsModel>(url);
  }

  public stopCampaign(specialOfferId: string) {
    const request = 'stopCampaign';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const data = { specialOfferId: specialOfferId };
    return this._apiService.postAsync<app.models.ServerResponseModel>(url, data, [
      SpecialOfferEvents.SpecialOfferStopped,
    ]);
  }

  public getFilteredClientSpecialOffers(pageFilter: app.models.GetSpecialOffersFilterModel) {
    const request = 'getFilteredClientSpecialOffers';
    let data = {
      ...pageFilter,
      fromDate: pageFilter.fromDate ? pageFilter.fromDate.toISOString() : null,
      toDate: pageFilter.toDate ? pageFilter.toDate.toISOString() : null,
    };
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.postGet<app.models.FilteredSpecialOfferClientModel[]>(url, data);
  }

  public getClientRequestedSpecialOffers(pageNumber: number) {
    const request = 'getClientRequestedSpecialOffers';
    let data = {
      page: pageNumber,
      size: DEFAULT_PAGE_SIZE,
    };
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.get<DynamicPaged<app.models.FilteredSpecialOfferClientModel>>(url, data);
  }

  public requestSpecialOffer(specialOfferId: app.models.SaveSpecialOfferModel) {
    const request = 'recordSpecialOfferEngagement';
    const data = { specialOfferId: specialOfferId, requestOffer: true };
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.postAsync<app.models.ServerResponseModel>(url, data, [
      SpecialOfferEvents.SpecialOfferEngagementRecorded,
    ]);
  }
}
